// src/pages/Movimientos.js
//
// Página global de movimientos:
//  - Tab Historial: tabla filtrable por tipo/almacén/producto/fechas.
//  - Tab Transferencias pendientes: cola con botones Aprobar / Rechazar.
//    El aprobador NO puede ser el mismo usuario que solicitó (validación backend).

import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Card, Typography, Stack, Tabs, Tab, TextField, MenuItem, Button, IconButton,
  Chip, Alert, CircularProgress, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import SwapHorizIcon    from '@mui/icons-material/SwapHoriz';
import CheckIcon        from '@mui/icons-material/Check';
import CloseIcon        from '@mui/icons-material/Close';
import RefreshIcon      from '@mui/icons-material/Refresh';
import HistoryIcon      from '@mui/icons-material/History';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { useApp } from '../context/AppContext';
import { movimientos as apiMov, almacenes as apiAlm } from '../api/posApi';

const TabPanel = ({ value, index, children }) => (
  <Box hidden={value !== index} sx={{ pt: 2 }}>{value === index && children}</Box>
);

const fmtNum = (n) => (Number(n) || 0).toLocaleString('es-CO', { maximumFractionDigits: 2 });
const fmtFecha = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleString('es-CO'); } catch { return s; }
};

const Movimientos = () => {
  const { empresa, sucursalActiva, notify } = useApp();
  const empresaId = empresa?.empresaId || empresa?.EmpresaId;

  const [tab, setTab]                 = useState(0);
  const [almacenes, setAlmacenes]     = useState([]);
  const [filtros, setFiltros]         = useState({
    almacenId: '', tipo: '', desde: '', hasta: '',
  });
  const [historial, setHistorial]     = useState([]);
  const [total, setTotal]             = useState(0);
  const [pendientes, setPendientes]   = useState([]);
  const [cargando, setCargando]       = useState(false);
  const [rechazando, setRechazando]   = useState(null); // {mov} para dialog

  // Traer almacenes de todas las sucursales
  useEffect(() => {
    (async () => {
      if (!empresa) return;
      // Cargamos por sucursal activa; si es Todas, hacemos por todas
      try {
        const lista = sucursalActiva
          ? await apiAlm.listar(sucursalActiva.sucursalId ?? sucursalActiva.SucursalId)
          : [];
        setAlmacenes(Array.isArray(lista) ? lista : []);
      } catch { setAlmacenes([]); }
    })();
  }, [empresa, sucursalActiva]);

  const cargarHistorial = useCallback(async () => {
    if (!empresaId) return;
    setCargando(true);
    try {
      const r = await apiMov.historial(empresaId, {
        ...filtros,
        almacenId: filtros.almacenId || undefined,
        tipo: filtros.tipo || undefined,
        desde: filtros.desde || undefined,
        hasta: filtros.hasta || undefined,
        page: 1, pageSize: 100,
      });
      setHistorial(r.data || []);
      setTotal(r.total || 0);
    } catch (e) {
      notify(e.mensajeUsuario || 'No se pudo cargar el historial.', 'error');
    } finally { setCargando(false); }
  }, [empresaId, filtros, notify]);

  const cargarPendientes = useCallback(async () => {
    if (!empresaId) return;
    try {
      const r = await apiMov.pendientes(empresaId);
      setPendientes(Array.isArray(r) ? r : []);
    } catch (e) {
      notify(e.mensajeUsuario || 'No se pudieron cargar las pendientes.', 'error');
    }
  }, [empresaId, notify]);

  useEffect(() => {
    if (tab === 0) cargarHistorial();
    if (tab === 1) cargarPendientes();
  }, [tab, cargarHistorial, cargarPendientes]);

  const aprobar = async (mov) => {
    try {
      await apiMov.aprobarTransferencia(empresaId, mov.movimientoId);
      notify('Transferencia aprobada. Stock movido.', 'success');
      await cargarPendientes();
    } catch (e) {
      notify(e.mensajeUsuario || 'No se pudo aprobar.', 'error');
    }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
        <HistoryIcon color="primary" fontSize="large" />
        <Box>
          <Typography variant="h4" fontWeight={700}>Movimientos de inventario</Typography>
          <Typography variant="body2" color="text.secondary">
            Historial + transferencias pendientes de aprobación
          </Typography>
        </Box>
      </Stack>

      <Card sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab icon={<HistoryIcon fontSize="small" />} iconPosition="start" label="Historial" />
          <Tab icon={<HourglassEmptyIcon fontSize="small" />} iconPosition="start"
               label={`Transferencias pendientes${pendientes.length ? ' (' + pendientes.length + ')' : ''}`} />
        </Tabs>
      </Card>

      <TabPanel value={tab} index={0}>
        <Card sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
            <TextField select size="small" label="Almacén" sx={{ minWidth: 200 }}
                       value={filtros.almacenId} onChange={(e) => setFiltros(f => ({ ...f, almacenId: e.target.value }))}>
              <MenuItem value="">Todos</MenuItem>
              {almacenes.map(a => (
                <MenuItem key={a.almacenId} value={a.almacenId}>{a.nombre}</MenuItem>
              ))}
            </TextField>
            <TextField select size="small" label="Tipo" sx={{ minWidth: 160 }}
                       value={filtros.tipo} onChange={(e) => setFiltros(f => ({ ...f, tipo: e.target.value }))}>
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="ENTRADA">Entradas</MenuItem>
              <MenuItem value="SALIDA">Salidas</MenuItem>
              <MenuItem value="AJUSTE">Ajustes</MenuItem>
              <MenuItem value="TRANSFERENCIA">Transferencias</MenuItem>
              <MenuItem value="RECEPCION">Recepciones</MenuItem>
              <MenuItem value="TOMA_FISICA">Toma física</MenuItem>
              <MenuItem value="VENTA">Ventas</MenuItem>
            </TextField>
            <TextField size="small" type="date" label="Desde" InputLabelProps={{ shrink: true }}
                       value={filtros.desde} onChange={(e) => setFiltros(f => ({ ...f, desde: e.target.value }))} />
            <TextField size="small" type="date" label="Hasta" InputLabelProps={{ shrink: true }}
                       value={filtros.hasta} onChange={(e) => setFiltros(f => ({ ...f, hasta: e.target.value }))} />
            <Button variant="contained" onClick={cargarHistorial}>Aplicar</Button>
            <IconButton onClick={cargarHistorial}><RefreshIcon /></IconButton>
          </Stack>
        </Card>

        <Card>
          {cargando ? (
            <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>
          ) : historial.length === 0 ? (
            <Alert severity="info" sx={{ m: 2 }}>Sin movimientos con estos filtros.</Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Producto</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Motivo</TableCell>
                    <TableCell>Almacén</TableCell>
                    <TableCell align="right">Cantidad</TableCell>
                    <TableCell align="right">Saldo</TableCell>
                    <TableCell>Usuario</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historial.map(m => (
                    <TableRow key={m.movimientoId}>
                      <TableCell><Typography variant="caption">{fmtFecha(m.fechaMovimiento)}</Typography></TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{m.productoNombre}</Typography>
                        <Typography variant="caption" color="text.secondary">{m.productoCodigo}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={m.tipoMovimiento}
                              color={m.tipoMovimiento === 'ENTRADA' ? 'success'
                                   : m.tipoMovimiento === 'SALIDA'  ? 'error'
                                   : m.tipoMovimiento === 'TRANSFERENCIA' ? 'info'
                                   : 'default'}
                              variant="outlined" />
                        {m.estadoTransferencia && (
                          <Chip size="small" label={m.estadoTransferencia}
                                sx={{ ml: 0.5, height: 20 }}
                                color={m.estadoTransferencia === 'APROBADA' ? 'success' :
                                       m.estadoTransferencia === 'RECHAZADA' ? 'error' : 'warning'} />
                        )}
                      </TableCell>
                      <TableCell><Typography variant="caption">{m.motivo || '—'}</Typography></TableCell>
                      <TableCell><Typography variant="caption">
                        {m.almacenNombre}
                        {m.almacenDestinoNombre && ` → ${m.almacenDestinoNombre}`}
                      </Typography></TableCell>
                      <TableCell align="right">{fmtNum(Math.abs(m.cantidad))}</TableCell>
                      <TableCell align="right">{m.stockNuevo != null ? fmtNum(m.stockNuevo) : '—'}</TableCell>
                      <TableCell><Typography variant="caption">{m.usuarioId}</Typography></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {historial.length > 0 && (
            <Box sx={{ p: 1.5, textAlign: 'right' }}>
              <Typography variant="caption" color="text.secondary">
                {historial.length} de {total} movimientos
              </Typography>
            </Box>
          )}
        </Card>
      </TabPanel>

      <TabPanel value={tab} index={1}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Las transferencias entre almacenes requieren autorización de un usuario distinto al que las solicitó.
          Al aprobar, el stock se mueve del origen al destino en una sola transacción.
        </Alert>
        {pendientes.length === 0 ? (
          <Card sx={{ p: 4, textAlign: 'center' }}>
            <SwapHorizIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">Sin transferencias pendientes.</Typography>
          </Card>
        ) : (
          <Stack spacing={1}>
            {pendientes.map(p => (
              <Card key={p.movimientoId} variant="outlined">
                <Stack direction="row" alignItems="center" spacing={2} sx={{ p: 2 }}>
                  <SwapHorizIcon color="info" fontSize="large" />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body2">
                      <b>{p.productoNombre}</b> ({p.productoCodigo})
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <b>{fmtNum(p.cantidad)}</b> unidades — {p.almacenNombre} → {p.almacenDestinoNombre}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Solicitado por {p.usuarioSolicita} el {fmtFecha(p.fechaMovimiento)}
                      {p.motivo && ` · Notas: ${p.motivo}`}
                    </Typography>
                  </Box>
                  <Tooltip title="Aprobar y mover stock">
                    <Button variant="contained" color="success" startIcon={<CheckIcon />}
                            onClick={() => aprobar(p)}>
                      Aprobar
                    </Button>
                  </Tooltip>
                  <Tooltip title="Rechazar">
                    <Button variant="outlined" color="error" startIcon={<CloseIcon />}
                            onClick={() => setRechazando(p)}>
                      Rechazar
                    </Button>
                  </Tooltip>
                </Stack>
              </Card>
            ))}
          </Stack>
        )}
      </TabPanel>

      {rechazando && (
        <DialogRechazo mov={rechazando} empresaId={empresaId}
                       onClose={() => setRechazando(null)}
                       onDone={async () => { setRechazando(null); await cargarPendientes(); notify('Transferencia rechazada.', 'info'); }}
                       onError={(m) => notify(m, 'error')} />
      )}
    </Box>
  );
};

function DialogRechazo({ mov, empresaId, onClose, onDone, onError }) {
  const [motivo, setMotivo] = useState('');
  const [ok, setOk] = useState(false);
  const submit = async () => {
    setOk(true);
    try {
      await apiMov.rechazarTransferencia(empresaId, mov.movimientoId, motivo || null);
      onDone();
    } catch (e) {
      onError(e.mensajeUsuario || 'No se pudo rechazar.');
    } finally { setOk(false); }
  };
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Rechazar transferencia</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {mov.productoNombre} · {fmtNum(mov.cantidad)} unid · {mov.almacenNombre} → {mov.almacenDestinoNombre}
        </Typography>
        <TextField fullWidth multiline rows={3} autoFocus label="Motivo de rechazo (opcional)"
                   value={motivo} onChange={(e) => setMotivo(e.target.value)} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={ok}>Cancelar</Button>
        <Button variant="contained" color="error" onClick={submit} disabled={ok}>
          {ok ? 'Rechazando…' : 'Rechazar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default Movimientos;
